import { TOOLTIP_DEFAULTS } from './tooltip.config';
import { ElementInfo, ViewportInfo } from './tooltip.interfaces';

// ✅ Utilidades para gestión de estado y cache del tooltip
export class TooltipUtils {
  
  /**
   * Detecta si el dispositivo es táctil
   */
  static detectTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
  
  /**
   * Detecta si es Safari (móvil o desktop)
   */
  static isSafari(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /safari/.test(userAgent) && !/chrome|crios|fxios|edg/.test(userAgent);
  }

  /**
   * Detecta si es Safari móvil específicamente
   */
  static isSafariMobile(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = this.isSafari();
    return isMobile && isSafari;
  }
  
  /**
   * Obtiene información del viewport actual
   */
  static getViewportInfo(): ViewportInfo {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth <= TOOLTIP_DEFAULTS.MOBILE_BREAKPOINT
    };
  }
  
  /**
   * Obtiene el scroll position corregido para Safari (móvil y desktop)
   */
  static getScrollPosition(): { scrollX: number; scrollY: number } {
    if (this.isSafari()) {
      // Safari (móvil y desktop): usar pageXOffset/pageYOffset como fallback
      return {
        scrollX: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0,
        scrollY: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      };
    }
    
    // Comportamiento estándar para otros navegadores
    return {
      scrollX: window.scrollX || document.documentElement.scrollLeft || 0,
      scrollY: window.scrollY || document.documentElement.scrollTop || 0
    };
  }
  
  /**
   * Detecta si el elemento o sus padres tienen positioning especial que puede afectar Safari
   */
  static hasPositioningIssues(element: HTMLElement): { hasAbsolute: boolean; hasTransform: boolean; hasZoom: boolean } {
    let current = element;
    let hasAbsolute = false;
    let hasTransform = false;
    let hasZoom = false;
    
    // Revisar elemento y padres hasta encontrar issues o llegar al body
    while (current && current !== document.body) {
      const computedStyle = window.getComputedStyle(current);
      
      if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') {
        hasAbsolute = true;
      }
      
      if (computedStyle.transform && computedStyle.transform !== 'none') {
        hasTransform = true;
      }
      
      // Verificar zoom de manera segura (no está en todas las definiciones TS)
      const zoomValue = (computedStyle as any).zoom;
      if (zoomValue && zoomValue !== '1' && zoomValue !== 'normal') {
        hasZoom = true;
      }
      
      current = current.parentElement as HTMLElement;
    }
    
    return { hasAbsolute, hasTransform, hasZoom };
  }

  /**
   * Obtiene información del elemento con mejor precisión
   */
  static getElementInfo(
    element: HTMLElement,
    cachedInfo: ElementInfo | null, 
    cacheValidUntil: number
  ): { elementInfo: ElementInfo; newCacheValidUntil: number } {
    const now = performance.now();
    
    // Usar cache si es válido
    if (cachedInfo && now < cacheValidUntil) {
      return { elementInfo: cachedInfo, newCacheValidUntil: cacheValidUntil };
    }
    
    // Calcular nueva información con mejor precisión
    const rect = element.getBoundingClientRect();
    const viewport = this.getViewportInfo();
    const scrollPosition = this.getScrollPosition();
    
    // Verificar que rect tenga valores válidos
    if (isNaN(rect.left) || isNaN(rect.top) || isNaN(rect.width) || isNaN(rect.height)) {
      console.warn('[TooltipMCDT] Invalid rect values detected, using fallbacks');
    }
    
    // Calcular espacios disponibles con más precisión
    const elementInfo: ElementInfo = {
      rect,
      spaceAbove: Math.max(0, rect.top),
      spaceBelow: Math.max(0, viewport.height - rect.bottom),
      spaceLeft: Math.max(0, rect.left),
      spaceRight: Math.max(0, viewport.width - rect.right)
    };

    // Debug mejorado
    /* if (this.isDebugEnabled()) {
      console.log('[TooltipMCDT Element Info Debug]', {
        element: {
          tagName: element.tagName,
          className: element.className,
          offsetParent: element.offsetParent?.tagName || 'none'
        },
        rect: {
          top: rect.top,
          left: rect.left,
          bottom: rect.bottom,
          right: rect.right,
          width: rect.width,
          height: rect.height
        },
        spaces: {
          above: elementInfo.spaceAbove,
          below: elementInfo.spaceBelow,
          left: elementInfo.spaceLeft,
          right: elementInfo.spaceRight
        },
        viewport: { width: viewport.width, height: viewport.height },
        scrollPosition
      });
    } */
    
    return {
      elementInfo,
      newCacheValidUntil: now + TOOLTIP_DEFAULTS.CACHE_DURATION
    };
  }
  
  /**
   * Valida que los inputs del directive sean correctos
   */
  static validateInputs(
    tooltipHtml: string,
    positionClass: string,
    validPositions: string[] = ['top', 'bottom', 'left', 'right']
  ): { isValid: boolean; correctedPosition?: string } {
    // Validar contenido
    if (!tooltipHtml?.trim()) {
      console.warn('TooltipMCDT: Empty tooltip content provided');
      return { isValid: false };
    }
    
    // Validar posición
    if (!validPositions.includes(positionClass)) {
      console.warn(`TooltipMCDT: Invalid position "${positionClass}". Using "top" instead.`);
      return { isValid: true, correctedPosition: 'top' };
    }
    
    return { isValid: true };
  }
  
  /**
   * Crea un timeout con limpieza automática
   */
  static createTimeout(
    callback: () => void,
    delay: number,
    currentTimeout: number | null
  ): number {
    // Limpiar timeout anterior si existe
    if (currentTimeout !== null) {
      window.clearTimeout(currentTimeout);
    }
    
    // Crear nuevo timeout
    return window.setTimeout(callback, delay);
  }
  
  /**
   * Limpia un timeout de forma segura
   */
  static clearTimeout(timeoutId: number | null): void {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
  
  /**
   * Verifica si el debug está habilitado (para optimizar rendimiento en producción)
   */
  static isDebugEnabled(): boolean {
    // Solo habilitar debug en desarrollo (puedes cambiar esta lógica)
    return !!(window as any).__tooltipDebug || false;
  }

  /**
   * Log de debugging condicional - OPTIMIZADO para rendimiento
   */
  static debugLog(message: string, enabled: boolean = false, ...args: any[]): void {
    if (enabled && this.isDebugEnabled()) {
      console.log(`[TooltipMCDT] ${message}`, ...args);
    }
  }
}
